import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDesignStore } from '../../store/designStore';
import { useAutoSave, saveDesignNow } from '../../hooks/useAutoSave';
import { useSharedDesignLoader } from '../../hooks/useSharedDesignLoader';
import { useDesignWip } from '../../hooks/useDesignWip';
import { DesignerHeader } from './DesignerHeader';
import { LeftSidebar, type ActiveToolId } from './LeftSidebar';
import { LeftToolPanel } from './LeftToolPanel';
import { TopToolbar } from './TopToolbar';
import { BottomBar } from './BottomBar';
import { RightPanel } from './RightPanel';
import { SideSelector } from '../canvas/SideSelector';
import { DesignerWorkspace } from '../canvas/DesignerWorkspace';
import { suppressAutoFit } from '../../hooks/useCanvasZoom';
import { PreviewView } from '../canvas/PreviewView';
import { Model3DPreview } from '../canvas/Model3DPreview';
import { Model3DPreviewMini } from '../canvas/Model3DPreviewMini';
import { useThinLineCheck } from '../../hooks/useThinLineCheck';
import { ThinLineWarningBanner } from '../canvas/ThinLineWarningBanner';
import { ThinLineDetailsModal } from '../canvas/ThinLineDetailsModal';
import { ImageCorrectionModal } from '../tools/ImageCorrectionModal';

interface DesignerAppProps {
  isPopupMode?: boolean;
  isAdminMode?: boolean;
  brandLogoUrl?: string | null;
  brandLogoLink?: string | null;
}

export function DesignerApp({ isPopupMode = false, isAdminMode = false, brandLogoUrl = null, brandLogoLink = null }: DesignerAppProps) {
  const { canvasInstances, activeSideKey, imageNaturalSizes, layoutConfig, productConfig, sides, dirtySideKeys } = useDesignStore();
  const activeCanvas = canvasInstances.get(activeSideKey) ?? null;
  const activeSide = sides.find(s => s.sideKey === activeSideKey);
  const activeImagePath = activeSide?.imagePath;
  const [activeTool, setActiveTool] = useState<ActiveToolId | null>('text');
  const [activeTab, setActiveTab] = useState<'design' | 'preview' | '3d'>('design');
  const [layerCount, setLayerCount] = useState(0);
  const workspaceRef = useRef<HTMLDivElement>(null);

  // Switches tabs IMMEDIATELY in the common case (no blocking/await — same
  // instant feel as a plain tab click today), and separately fires a save
  // right away, in the background, whenever landing on the Preview tab with
  // unsaved edits. Closes the real bug documented on designStore.ts's
  // dirtySideKeys field: without this, a customer who edits a position and
  // immediately clicks Next/Preview would wait up to 30s (useAutoSave's own
  // interval) before a fresh embossing/embroidery render even started —
  // PreviewView.tsx's own dirtySideKeys check (see that file) is what makes
  // the STALE render disappear instantly; this is what makes the REAL new
  // one actually start being built without a long wait. Fire-and-forget
  // deliberately: this click handler must never await a NETWORK request
  // before the tab switch is visible.
  //
  // REAL BUG found+fixed 2026-08-28 (live user report: add image 1, remove,
  // add image 2, immediately switch to Preview then back to Design — image 1
  // reappeared): the ONE deliberate exception to the "never wait" rule above
  // — see designStore.ts's own pendingCanvasCommits field comment for the
  // full mechanism this closes. This is a short poll against local in-memory
  // state (not a network request), normally resolving in well under 1s,
  // guarding against DesignerWorkspace's own unmount (which happens on every
  // switch away from the Design tab) disposing the canvas before an
  // in-flight image/QR/background commit's async callback has mutated it.
  const handleTabChange = useCallback((tab: 'design' | 'preview' | '3d') => {
    const doSwitch = () => {
      setActiveTab(tab);
      if (tab === 'preview' && useDesignStore.getState().dirtySideKeys.size > 0) saveDesignNow();
    };
    if (useDesignStore.getState().pendingCanvasCommits > 0) {
      const poll = () => {
        if (useDesignStore.getState().pendingCanvasCommits > 0) setTimeout(poll, 50);
        else doSwitch();
      };
      poll();
    } else {
      doSwitch();
    }
  }, [dirtySideKeys]);

  const thinLineCheck = useThinLineCheck(activeSide);
  const [showThinLineModal, setShowThinLineModal] = useState(false);
  // The object staged for re-editing via "Upload another" (see
  // ThinLineDetailsModal's onReplace) — reuses ImageCorrectionModal.tsx's
  // existing "Correct Colors" re-edit wizard rather than building a
  // parallel replace flow, since committing through it already re-triggers
  // a fresh thin-line check the same way any other placement does.
  const [correctionTarget, setCorrectionTarget] = useState<any | null>(null);

  // If the active tab gets disabled by layout config, fall back to design tab.
  // `display3dModel === false` is the per-product admin override (e.g. a
  // product whose UV wrapping isn't verified yet) — same effect as the
  // layout-level toggle, just scoped to one product instead of all of them.
  useEffect(() => {
    if (activeTab === 'preview' && layoutConfig.previewTabEnabled === false) setActiveTab('design');
    if (activeTab === '3d' && (layoutConfig.preview3dTabEnabled === false || productConfig?.display3dModel === false)) setActiveTab('design');
  }, [activeTab, layoutConfig.previewTabEnabled, layoutConfig.preview3dTabEnabled, productConfig?.display3dModel]);

  useAutoSave();
  useSharedDesignLoader();
  useDesignWip();

  // Layer count badge on the sidebar's "Layers" icon — same object-count
  // source LayerPanel itself uses, just a count instead of the full list.
  useEffect(() => {
    if (!activeCanvas) { setLayerCount(0); return; }
    const fc = activeCanvas as any;
    const updateCount = () => {
      const objects: any[] = fc.getObjects?.() ?? [];
      setLayerCount(objects.filter(obj => !obj.isTemplateBackground).length);
    };
    fc.on('object:added', updateCount);
    fc.on('object:removed', updateCount);
    fc.on('history:restored', updateCount);
    updateCount();
    return () => {
      fc.off('object:added', updateCount);
      fc.off('object:removed', updateCount);
      fc.off('history:restored', updateCount);
    };
  }, [activeCanvas]);

  const handleToolSelect = (id: ActiveToolId) => {
    setActiveTool(prev => (prev === id ? null : id));
  };

  // Delete/Backspace deletes the selected canvas object — mirrors the trash icon in
  // TopToolbar, but from the keyboard. Skipped while focus is in a text input/textarea
  // (e.g. typing in TextTool's textarea or a PropertyPanel number field) so Backspace
  // there edits the field's text instead of deleting the whole object out from under it.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (!activeCanvas) return;
      const active = (activeCanvas as any).getActiveObject?.();
      if (!active) return;
      e.preventDefault();
      if ((activeCanvas as any).getActiveObjects) {
        const objs = (activeCanvas as any).getActiveObjects();
        objs.forEach((o: any) => activeCanvas.remove(o));
        (activeCanvas as any).discardActiveObject();
      } else {
        activeCanvas.remove(active);
      }
      activeCanvas.requestRenderAll?.() ?? (activeCanvas as any).renderAll();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeCanvas]);

  const fitToViewport = useCallback(() => {
    if (!activeCanvas || !workspaceRef.current) return;
    const container = workspaceRef.current;
    const availW = container.clientWidth - 48;
    const availH = container.clientHeight - 48;
    if (availW <= 0 || availH <= 0) return;
    const fc = activeCanvas as any;
    const originalW = fc._originalWidth ?? fc.width ?? availW;
    const originalH = fc._originalHeight ?? fc.height ?? availH;
    // The design-area rectangle (canvas) is often much smaller than the actual product
    // photo — fit against whichever is bigger so an oversized photo never overflows
    // the viewport, even though the canvas itself would technically fit at zoom 1.
    // Keyed by `imagePath` (not sideKey) — see designStore.ts's imageNaturalSizes doc.
    const natural = activeImagePath ? imageNaturalSizes.get(activeImagePath) : undefined;
    const fitW = natural ? Math.max(natural.w, originalW) : originalW;
    const fitH = natural ? Math.max(natural.h, originalH) : originalH;
    const zoom = Math.max(0.05, Math.min(availW / fitW, availH / fitH, 1));
    fc.setZoom(zoom);
    fc.setDimensions({ width: originalW * zoom, height: originalH * zoom });
    fc.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
    fc.renderAll();
  }, [activeCanvas, activeImagePath, imageNaturalSizes]);

  // The product photo loads asynchronously, separately from canvas creation, so its
  // real size often isn't known yet when the initial fit-to-viewport runs. Re-fit
  // automatically once we learn it — this is what shrinks an oversized photo to fit.
  const naturalForSide = activeImagePath ? imageNaturalSizes.get(activeImagePath) : undefined;
  useEffect(() => {
    fitToViewport();
  }, [naturalForSide?.w, naturalForSide?.h, activeCanvas]);

  // The left tool panel and other layout chrome animate their width/height (e.g. opening
  // the Clipart panel narrows the workspace by 280px) without changing any of the deps
  // above, so the canvas would otherwise keep rendering at a stale size/zoom and get
  // visually clipped by the now-smaller container until the user manually re-fits.
  //
  // Skipped while `suppressAutoFit` is set — a manual zoom step that crosses the
  // "content no longer fits" threshold makes this same container grow a scrollbar,
  // which shrinks its own clientWidth/clientHeight (the scrollbar gutter) and fires
  // this observer too. Without the guard, that self-triggered fire called
  // `fitToViewport()` and silently reset the zoom the user just asked for back down
  // to whatever fits, on every zoom-in step that crossed that threshold — the
  // canvas would jump to the new zoom for one frame, then snap back, forever stuck
  // just below the "needs a scrollbar" size. See `useCanvasZoom.ts`'s `markManualZoom`.
  useEffect(() => {
    if (!workspaceRef.current) return;
    const ro = new ResizeObserver(() => {
      if (suppressAutoFit) return;
      fitToViewport();
    });
    ro.observe(workspaceRef.current);
    return () => ro.disconnect();
  }, [fitToViewport]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <DesignerHeader
        isPopupMode={isPopupMode}
        isAdminMode={isAdminMode}
        brandLogoUrl={brandLogoUrl}
        brandLogoLink={brandLogoLink}
        onCartClick={() => {
          // "Next" (Design tab) sends the customer to the Preview tab (2D, grid
          // mode). Once there, this same header button switches in place to
          // "Finish Design" (see DesignerHeader's own isFinishStep branch) —
          // this handler only fires for the Design-tab "Next" click itself.
          handleTabChange('preview');
        }}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {activeTab === 'design' ? (
        <>
          <TopToolbar canvas={activeCanvas} />

          <div style={{ display: 'flex', flex: 1,minHeight: 0, overflow: 'hidden' }}>
            {/* Left vertical sidebar — 68px fixed */}
            <LeftSidebar activeTool={activeTool} onToolSelect={handleToolSelect} layerCount={layerCount} />

            {/* Left tool panel — slides in/out (280px) */}
            <LeftToolPanel activeTool={activeTool} canvas={activeCanvas} />

            {/* Canvas zone — flex:1 */}
            {/* <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f0f0f0' }}> */}
            <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f0f0f0' }}>
              <SideSelector />
              <ThinLineWarningBanner flags={thinLineCheck.flags} onSeeDetails={() => setShowThinLineModal(true)} />
              {/* `overflow: 'auto'` (was `'hidden'`) — a zoomed-in design that's bigger than
                  this box needs real scrollbars to reach the parts that don't fit, instead
                  of silently clipping them with no way to view the rest. See the comment on
                  `zoomCanvasAt` in `useCanvasZoom.ts` for why panning happens by scrolling
                  this container rather than inside the Fabric canvas itself. */}
              <div ref={workspaceRef}  style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                <DesignerWorkspace containerRef={workspaceRef} />
              </div>
              <BottomBar canvas={activeCanvas} onFitToViewport={fitToViewport} containerRef={workspaceRef} />
            </div>

            {/* Right panel — 260px fixed — Colors Used + Layers. Clicking a
                layer switches the LEFT tool panel into its 'edit' tool
                (PropertyPanel) for that object. */}
            <RightPanel canvas={activeCanvas} onEditLayer={() => setActiveTool('edit')} />
          </div>

          {/* Floating "min version" 3D preview — only when the 3D tab is enabled
              and a GLB model exists; hidden if the layout disables the 3D tab,
              or if the product's own "Display 3D" admin flag is off. */}
          {productConfig?.glbModelUrl && productConfig?.display3dModel !== false && layoutConfig.preview3dTabEnabled !== false && (
            <Model3DPreviewMini onExpand={() => setActiveTab('3d')} />
          )}
        </>
      ) : activeTab === 'preview' ? (
        // Read-only "what the customer gets" view — no editing chrome, since there's
        // no canvas selection concept to act on here. PreviewView owns its own
        // grid/slider side navigation, so no <SideSelector /> here. The finish/save
        // action itself now lives in the header (DesignerHeader's "Finish Design"
        // step), not in this view — see DesignerApp's onCartClick above.
        <PreviewView initialViewMode="grid" />
      ) : (
        // Live 3D view — the active side's composited design applied as a texture
        // onto the product's uploaded GLB model.
        <Model3DPreview />
      )}

      {showThinLineModal && thinLineCheck.flags.length > 0 && (
        <ThinLineDetailsModal
          flags={thinLineCheck.flags}
          onClose={() => setShowThinLineModal(false)}
          onReplace={flag => {
            setShowThinLineModal(false);
            if (flag.object) setCorrectionTarget(flag.object);
          }}
        />
      )}

      {correctionTarget && (
        <ImageCorrectionModal
          canvas={activeCanvas}
          existingObject={correctionTarget}
          onClose={() => setCorrectionTarget(null)}
        />
      )}
    </div>
  );
}
